import { GoogleMap, Marker } from '@react-google-maps/api';
import { memo, useCallback, useEffect, useState } from 'react';
import { CollectionPointWithI18nKey } from '../../../../types/collectionPoints';
import { config, getAssetUrl } from '../../../../utils/config';
import LoadingIndicator from '../../../atoms/LoadingIndicator';
import './style.scss';

type MapProps = {
  locations?: CollectionPointWithI18nKey[];
  selectedLocationDetails?: CollectionPointWithI18nKey;
  onMarkerClick?: (id: string) => void;
  isGoogleMapsSdkLoaded: boolean;
  view?: 'map' | 'list';
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
};

const DEFAULT_INITIAL_CENTER_COORDINATES = { latitude: 53.5483197, longitude: 10.0006301 };

const Map = ({ locations, selectedLocationDetails, onMarkerClick, isGoogleMapsSdkLoaded, view }: MapProps) => {
  const [map, setMap] = useState<google.maps.Map>();

  const getBounds = () => {
    const bounds = new google.maps.LatLngBounds();
    locations?.forEach(location => {
      const coordinates = location.displayData.coordinates;
      bounds.extend(new google.maps.LatLng(coordinates.latitude, coordinates.longitude));
    });
    return bounds;
  };

  const onLoad = useCallback((_map: google.maps.Map) => {
    const { latitude, longitude } =
      config.availableCountries.shipping[0].centerCoordinates ?? DEFAULT_INITIAL_CENTER_COORDINATES;
    _map.setCenter(new google.maps.LatLng(latitude, longitude));
    _map.setZoom(5);

    if (locations?.length) {
      _map?.fitBounds(getBounds());
    }
    setMap(_map);
  }, []);

  useEffect(() => {
    if (!locations?.length || view === 'list') {
      return;
    }
    map?.fitBounds(getBounds());
  }, [locations, view]);

  const onUnmount = useCallback(() => {
    setMap(undefined);
  }, []);

  if (!isGoogleMapsSdkLoaded) {
    return (
      <div className="map-container">
        <LoadingIndicator />
      </div>
    );
  }

  if (selectedLocationDetails) {
    const { latitude, longitude } = selectedLocationDetails.displayData.coordinates;

    return (
      <GoogleMap
        mapContainerClassName="map-container"
        clickableIcons={false}
        options={{
          ...mapOptions,
          zoom: 14,
          center: { lat: latitude, lng: longitude },
        }}
      >
        <Marker
          position={{ lat: latitude, lng: longitude }}
          options={{
            icon: {
              scaledSize: new google.maps.Size(50, 50),
              url: getAssetUrl(`collectionPoints/mapPins/${selectedLocationDetails.i18nKey}Selected.svg`),
            },
          }}
        />
      </GoogleMap>
    );
  }

  return (
    <GoogleMap
      mapContainerClassName="map-container"
      clickableIcons={false}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={mapOptions}
    >
      {locations?.map(location => {
        const { latitude, longitude } = location.displayData.coordinates;
        const { collectionPointId } = location.specification;

        return (
          <Marker
            key={collectionPointId}
            position={{ lat: latitude, lng: longitude }}
            onClick={() => onMarkerClick?.(collectionPointId)}
            options={{
              icon: {
                scaledSize: new google.maps.Size(50, 50),
                url: getAssetUrl(`collectionPoints/mapPins/${location.i18nKey}.svg`),
              },
            }}
          />
        );
      })}
      ;
    </GoogleMap>
  );
};

export default memo(Map);
